import Entity from "./entity";
import Documents from "./documents";
import IRecordResponse from "../interface/record-response";
import ILinkedInactive from "../interface/linked-inactive";
import ILinkedFieldOption from "../interface/linked-field-option";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ErrorCode from "../enum/error-code";
import RecordClass from "../class/record";

class RecordInstance {

    private Record: RecordClass | null = null;

    /* Public Methods - Init */

    public async init(entityId: number, recordId: number): Promise<IRecordResponse> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity && Entity.hasAccess(entityId)) {
            const doc: IDocument | null = await Documents.loadOne(entityId, recordId);
            if (doc) {
                this.clear();
                await Documents.loadRelatedDocs(entity, [doc]);
                this.Record = new RecordClass(entity, doc);
                return this.Record.getClientResponse();
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    /* Public Methods - Documents control */

    public async duplicate(): Promise<IDocument> {
        return await this.get().documentDuplicate();
    }

    public async update(docId: number, fieldId: number, value: IDocumentValue): Promise<IDocument> {
        return await this.get().documentUpdate(docId, fieldId, value);
    }

    public getDefaultValue(fieldId: number): IDocumentValue | null {
        return this.get().getDefaultValue(fieldId);
    }

    public async delete(ids: number[]): Promise<number[]> {
        if (ids.includes(this.get().recordId)) {
            const result: number[] = await Documents.delete(this.get().entityId, ids);
            this.clear();
            return result;
        }
        return [];
    }

    /* Private Public Methods - Notification handlers */

    public onDocumentsUpdate(entityId: number, docs: IDocument[]): void {
        if (this.Record && entityId === this.Record.entityId) {
            this.Record.onDocumentsUpdate(docs);
        }
    }

    public async onDocumentsReset(entityId: number, docs: IDocument[]): Promise<void> {
        if (this.Record && entityId === this.Record.entityId) {
            await this.Record.onDocumentsUpdate(docs);
        }
    }

    public onEntityReset(entityIds: number[]): void {
        if (this.Record) {
            if (entityIds.includes(this.Record.entityId)) {
                this.Record.onEntityReset();
            }
        }
    }

    /* Public Methods - Linked Field calls */

    public isLinkedActive(fieldId: number): ILinkedInactive[] {
        return this.get().isLinkedActive(fieldId);
    }

    public async getLinkedOptions(fieldId: number): Promise<ILinkedFieldOption[] | null> {
        return this.get().getLinkedOptions(fieldId);
    }

    /* Public Methods - Special calls */

    public async getSimilarValues(fieldId: number, ptrn: string): Promise<string[]> {
        return this.get().getSimilarValues(fieldId, ptrn);
    }


    /* Private Public Methods - Utility */

    public clear(): void {
        if (this.Record) {
            this.Record.deactivate();
            this.Record = null;
        }
    }

    /* Private Methods */

    private get(): RecordClass {
        if (this.Record) {
            return this.Record;
        } else {
            throw ErrorCode.NotInitialized;
        }
    }

}

const Record = new RecordInstance();

export default Record;